import { ApiErrorHandler, ErrorCodes } from '@/lib/error-handler'

/**
 * Centralized error tracking system
 * Provides consistent error logging and monitoring across the application
 */

export interface ErrorContext {
  component?: string
  action?: string
  userId?: string
  route?: string
  userAgent?: string
  timestamp?: number
  additional?: Record<string, any>
}

export interface TrackedError {
  id: string
  error: Error
  context: ErrorContext
  timestamp: number
  stackTrace?: string
  apiErrorCode?: ErrorCodes
  apiErrorMessage?: string
}

class ErrorTracker {
  private errors: TrackedError[] = []
  private maxErrors = 100 // Keep last 100 errors
  private isDevelopment = import.meta.env.DEV

  /**
   * Track an error with context
   */
  track(error: Error | string, context: ErrorContext = {}): string {
    const errorObj = typeof error === 'string' ? new Error(error) : error
    const errorId = this.generateErrorId()

    // Use ApiErrorHandler to extract API error information
    const apiHandler = new ApiErrorHandler(errorObj)
    const apiErrorCode = apiHandler.getErrorCode()
    const apiErrorMessage = apiHandler.getUserFriendlyMessage()

    const trackedError: TrackedError = {
      id: errorId,
      error: errorObj,
      context: {
        timestamp: Date.now(),
        route: window.location.pathname,
        userAgent: navigator.userAgent,
        ...context
      },
      timestamp: Date.now(),
      stackTrace: errorObj.stack,
      apiErrorCode,
      apiErrorMessage
    }

    // Add to local collection
    this.errors.unshift(trackedError)

    // Keep only recent errors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors)
    }

    return errorId
  }

  /**
   * Track component errors with user-friendly messages
   */
  trackComponentError(
    error: Error,
    component: string,
    action: string,
    showToast = true,
    context: ErrorContext = {}
  ): string {
    const apiHandler = new ApiErrorHandler(error)
    const errorCode = apiHandler.getErrorCode()
    const userMessage = apiHandler.getUserFriendlyMessage()

    // Show user-friendly toast if needed (skip for 2FA required error and common errors that shouldn't show toast)
    if (showToast && typeof window !== 'undefined' && errorCode !== 7004) {
      // Dynamic import to avoid circular dependencies
      import('@/composables/useToast').then(({ useToast }) => {
        const { showError } = useToast()
        showError(userMessage)
      })
    }

    const componentContext: ErrorContext = {
      ...context,
      component,
      action,
      additional: {
        userMessage,
        errorCode,
        ...context.additional
      }
    }

    return this.track(error, componentContext)
  }

  /**
   * Track navigation errors
   */
  trackNavigationError(error: Error, from: string, to: string, context: ErrorContext = {}): string {
    const navContext: ErrorContext = {
      ...context,
      component: 'Navigation',
      action: `Navigate from ${from} to ${to}`,
      additional: {
        from,
        to
      }
    }

    return this.track(error, navContext)
  }

  /**
   * Track chart/UI errors
   */
  trackUIError(
    error: Error,
    component: string,
    action: string,
    context: ErrorContext = {}
  ): string {
    const uiContext: ErrorContext = {
      ...context,
      component,
      action,
      additional: {
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      }
    }

    return this.track(error, uiContext)
  }

  /**
   * Get recent errors for debugging
   */
  getRecentErrors(limit = 10): TrackedError[] {
    return this.errors.slice(0, limit)
  }

  /**
   * Get errors by component
   */
  getErrorsByComponent(component: string): TrackedError[] {
    return this.errors.filter((error) => error.context.component === component)
  }

  /**
   * Clear all stored errors
   */
  clear(): void {
    this.errors = []
  }

  /**
   * Generate unique error ID
   */
  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Get error summary for debugging
   */
  getSummary(): {
    totalErrors: number
    errorsByComponent: Record<string, number>
    recentErrors: TrackedError[]
  } {
    const errorsByComponent: Record<string, number> = {}

    this.errors.forEach((error) => {
      const component = error.context.component || 'Unknown'
      errorsByComponent[component] = (errorsByComponent[component] || 0) + 1
    })

    return {
      totalErrors: this.errors.length,
      errorsByComponent,
      recentErrors: this.getRecentErrors(5)
    }
  }
}

// Export singleton instance (for internal use)
const errorTracker = new ErrorTracker()

// Export convenience functions
export const trackError = (error: Error | string, context?: ErrorContext) =>
  errorTracker.track(error, context)

export const trackNavigationError = (
  error: Error,
  from: string,
  to: string,
  context?: ErrorContext
) => errorTracker.trackNavigationError(error, from, to, context)

export const trackComponentError = (
  error: Error,
  component: string,
  action: string,
  showToast?: boolean,
  context?: ErrorContext
) => errorTracker.trackComponentError(error, component, action, showToast, context)

export const trackUIError = (
  error: Error,
  component: string,
  action: string,
  context?: ErrorContext
) => errorTracker.trackUIError(error, component, action, context)
